import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { HiBell, HiCheck, HiCheckCircle, HiExternalLink } from 'react-icons/hi'

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function NotificationDropdown({
  open,
  onClose,
  notifications,
  loading,
  onFetch,
  onMarkRead,
  onMarkAllRead,
  unreadCount,
}) {
  const dropdownRef = useRef(null)
  const navigate = useNavigate()

  // Load notifications when dropdown opens
  useEffect(() => {
    if (open) onFetch()
  }, [open, onFetch])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 max-h-[28rem] bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-xl z-50 flex flex-col animate-fade-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-700">
        <div className="flex items-center gap-2">
          <HiBell className="w-4 h-4 text-brand-500" />
          <span className="text-sm font-semibold text-surface-800 dark:text-surface-200">
            Notifications
          </span>
          {unreadCount > 0 && (
            <span className="text-xs bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 px-1.5 py-0.5 rounded-full font-medium">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllRead}
            className="text-xs text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1"
          >
            <HiCheckCircle className="w-3.5 h-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && notifications.length === 0 && (
          <div className="flex items-center justify-center py-10 text-surface-400 text-sm">
            Loading…
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-1">
            <HiBell className="w-8 h-8 text-surface-300 dark:text-surface-600" />
            <span className="text-sm text-surface-400 dark:text-surface-500">No notifications yet</span>
          </div>
        )}

        {notifications.map((n) => (
          <button
            key={n.id}
            onClick={() => {
              if (!n.is_read) onMarkRead(n.id)
              if (n.link) {
                navigate(n.link)
                onClose()
              }
            }}
            className={`w-full text-left px-4 py-3 border-b border-surface-100 dark:border-surface-700/50 hover:bg-surface-50 dark:hover:bg-surface-700/60 transition-colors ${
              !n.is_read ? 'bg-brand-50/40 dark:bg-brand-900/10' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              {/* Unread dot */}
              <div className="mt-1.5 shrink-0">
                {!n.is_read ? (
                  <span className="block w-2 h-2 bg-brand-500 rounded-full" />
                ) : (
                  <span className="block w-2 h-2" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-surface-800 dark:text-surface-100' : 'text-surface-700 dark:text-surface-300'}`}>
                  {n.title}
                </p>
                {n.body && (
                  <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5 truncate">
                    {n.body}
                  </p>
                )}
                <p className="text-[11px] text-surface-400 dark:text-surface-500 mt-1">
                  {timeAgo(n.created_at)}
                </p>
              </div>

              {n.link && (
                <HiExternalLink className="w-3.5 h-3.5 text-surface-400 mt-1 shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
